/**
 * Action Schema Types for Gibbs Action Registry
 *
 * Defines the shape of action definitions, parameter definitions,
 * and precondition/postcondition types.
 *
 * @module gibbs/registry/schema
 */

/**
 * @typedef {'interactive' | 'short' | 'long' | 'background'} RuntimeClass
 * - interactive: <1s, UI-level response
 * - short: 1-30s, synchronous server call
 * - long: 30s-5min, blocking computation
 * - background: minutes to hours, async job via Temporal
 */

/**
 * @typedef {'implemented' | 'planned'} ActionStatus
 */

/**
 * @typedef {Object} ParamDef
 * @property {string} name - Parameter name
 * @property {string} type - Workflow type name (from WorkflowTypes)
 *   or primitive ('string', 'number', 'boolean', 'object')
 * @property {string} description - Human-readable description
 * @property {boolean} required - Whether this parameter must be provided
 * @property {*} [defaultValue] - Default value if not provided
 * @property {string[]} [options] - Valid enum options
 * @property {Object<string,string>} [optionLabels] - Optional display labels
 *   for enum options; stored values remain the keys in `options`
 * @property {{ min?: number, max?: number, pattern?: string }} [constraints]
 *   Validation constraints
 */

/**
 * @typedef {Object} PreCondition
 * @property {string} description - Human-readable description (e.g., "A protein must be loaded")
 * @property {string} check - Symbolic check ID (e.g., "protein_loaded")
 */

/**
 * @typedef {Object} PostCondition
 * @property {string} description - Human-readable description
 *   (e.g., "Compound energies are updated")
 * @property {string} guarantee - Symbolic guarantee ID (e.g., "compound_has_energies")
 */

/**
 * @typedef {Object} ActionDef
 * @property {string} id - Unique kebab-case identifier (e.g., "dock-compound")
 * @property {string} name - Human-readable display name
 * @property {string} category - Pipeline category (e.g., "Target Setup", "Fragment Operations")
 * @property {string} description - What this action does (1-3 sentences)
 * @property {ParamDef[]} inputs - Required typed inputs
 * @property {ParamDef[]} outputs - Typed outputs produced
 * @property {ParamDef[]} parameters - Configurable options with defaults
 * @property {string[]} dependsOn - Task IDs that should run before this (soft dependencies)
 * @property {PreCondition[]} preconditions - Workspace state checks that must pass
 * @property {PostCondition[]} postconditions - State guarantees after successful execution
 * @property {RuntimeClass} runtime - Execution time classification
 * @property {ActionStatus} status - Whether this action is implemented or planned
 * @property {string|null} userAction - UserActions key (e.g., "EnergyMinimize") or null
 * @property {string|null} serverCommand - Server command name (e.g., "energy-minimize") or null
 * @property {string|null} jobType - JobTypes key (e.g., "folding") or null
 * @property {boolean} [destructiveConfirm] - When true, the chain
 *   intercepts dispatch and emits a confirm clarify before invoking
 *   the underlying UserCmd. From `gibbs-destructive-ops-ux` (2026-05-03).
 *   The action MUST also set `confirmMessage`. Not allowed on
 *   `runtime: 'long'` actions (confirm latency would compound).
 * @property {string} [confirmMessage] - Required when
 *   `destructiveConfirm: true`. The user-facing warning text the
 *   confirm clarify shows on turn 1. MUST be ≥ 40 chars and name
 *   specifically what data will be lost.
 */

// ─── Valid Values ────────────────────────────────────────────────────────────

/** @type {readonly RuntimeClass[]} */
export const VALID_RUNTIMES = Object.freeze(['interactive', 'short', 'long', 'background']);

/** @type {readonly ActionStatus[]} */
export const VALID_STATUSES = Object.freeze(['implemented', 'planned']);

/** @type {readonly string[]} */
export const CATEGORIES = Object.freeze([
    'Target Setup',
    'Compound Management',
    'Fragment Operations',
    'Simulation & Computation',
    'Distributed Jobs',
    'Property Prediction',
    'Search & Filtering',
    'Analysis & Visualization',
    'Export & Reporting',
    'Workspace Management',
    'External Data Retrieval',
    'Planned — Aim 1',
    'Planned — Aim 2',
    'Medchem Design',
    'Display',
]);

// ─── Schema Validation ──────────────────────────────────────────────────────

/**
 * Validate an ActionDef object against the schema. Returns an array of
 * error messages (empty if valid).
 *
 * @param {ActionDef} action
 * @returns {string[]} Validation errors
 */
export function validateActionDef(action) {
    const errors = [];

    if (!action.id || typeof action.id !== 'string') errors.push('id is required and must be a string');
    if (!action.name || typeof action.name !== 'string') errors.push('name is required');
    if (!action.category || typeof action.category !== 'string') errors.push('category is required');
    if (!action.description || typeof action.description !== 'string') errors.push('description is required');
    if (!Array.isArray(action.inputs)) errors.push('inputs must be an array');
    if (!Array.isArray(action.outputs)) errors.push('outputs must be an array');
    if (!Array.isArray(action.parameters)) errors.push('parameters must be an array');
    if (!Array.isArray(action.dependsOn)) errors.push('dependsOn must be an array');
    if (!Array.isArray(action.preconditions)) errors.push('preconditions must be an array');
    if (!Array.isArray(action.postconditions)) errors.push('postconditions must be an array');
    if (!VALID_RUNTIMES.includes(action.runtime)) errors.push(`runtime must be one of: ${VALID_RUNTIMES.join(', ')}`);
    if (!VALID_STATUSES.includes(action.status)) errors.push(`status must be one of: ${VALID_STATUSES.join(', ')}`);

    // Execution reference check for implemented actions
    if (action.status === 'implemented') {
        if (!action.userAction && !action.serverCommand && !action.jobType) {
            errors.push('implemented actions must have at least one execution reference (userAction, serverCommand, or jobType)');
        }
    }

    // Destructive-confirm field validation
    // (from gibbs-destructive-ops-ux, 2026-05-03)
    if (action.destructiveConfirm === true) {
        if (typeof action.confirmMessage !== 'string'
            || action.confirmMessage.length < 40) {
            errors.push(
                'destructiveConfirm: true requires a confirmMessage of '
                + '≥ 40 chars naming the data that will be lost'
            );
        }
        if (action.runtime === 'long') {
            errors.push(
                'destructiveConfirm: true is not allowed on long-runtime '
                + 'actions; confirm latency would compound the wait'
            );
        }
    }

    return errors;
}

/**
 * Helper to create a ParamDef with defaults.
 *
 * @param {string} name
 * @param {string} type
 * @param {string} description
 * @param {boolean} [required=true]
 * @param {Object} [extras] - Additional fields: defaultValue, options, constraints
 * @returns {ParamDef}
 */
export function param(name, type, description, required = true, extras = {}) {
    return {
        name, type, description, required, ...extras,
    };
}

/**
 * Helper to create a PreCondition.
 *
 * @param {string} check - Symbolic check ID
 * @param {string} description - Human-readable description
 * @returns {PreCondition}
 */
export function pre(check, description) {
    return { check, description };
}

/**
 * Helper to create a PostCondition.
 *
 * @param {string} guarantee - Symbolic guarantee ID
 * @param {string} description - Human-readable description
 * @returns {PostCondition}
 */
export function post(guarantee, description) {
    return { guarantee, description };
}
